/** API helpers for PitchAI public presentation pages. */

import { getStoredAccessToken } from "@/lib/adminApi";

const API_BASE =
  (process.env.REACT_APP_API_URL || "").replace(/\/$/, "") ||
  (typeof window !== "undefined" ? "" : "http://127.0.0.1:8000");

function apiUrl(path: string): string {
  if (API_BASE) return `${API_BASE}${path}`;
  // Dev: CRA proxy (package.json) or same-origin when reverse-proxied
  return path;
}

export interface PublicPresentationResponse {
  id: string;
  slug: string;
  status: string;
  presentation_data: any;
}

export async function fetchPublicPresentation(
  slug: string
): Promise<PublicPresentationResponse | null> {
  try {
    const res = await fetch(apiUrl(`/api/public/presentations/${encodeURIComponent(slug)}`));
    if (res.status === 404) return null;
    if (!res.ok) return null;
    return (await res.json()) as PublicPresentationResponse;
  } catch {
    return null;
  }
}

export function getOrCreateSessionId(): string {
  const key = "pitchai_sid";
  try {
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const id = `s_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(key, id);
    return id;
  } catch {
    return `s_${Math.random().toString(36).slice(2, 10)}`;
  }
}

/**
 * Fire-and-forget presentation analytics.
 *
 * Authenticated PitchAI admin sessions (Alain) never record events — including
 * when opening /p/:slug?preview=1 from /admin. `preview=1` alone does not
 * suppress tracking for anonymous visitors.
 */
export function trackPresentationEvent(
  slug: string,
  eventType: "page_view" | "cta_schedule" | "cta_sms" | "cta_email",
  metadata: Record<string, unknown> = {}
): void {
  const adminToken = getStoredAccessToken();
  if (adminToken) {
    // Client-side skip for admin previews; backend also ignores admin Bearer.
    return;
  }

  const body = JSON.stringify({
    event_type: eventType,
    session_id: getOrCreateSessionId(),
    metadata,
  });
  const url = apiUrl(`/api/public/presentations/${encodeURIComponent(slug)}/events`);
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    }
  } catch {
    // fall through
  }
  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => undefined);
}
